import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, CustomerPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ArStatusChip, fmtDate, getErrorMessage, unwrap } from './ArShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const studentLabel = (p, look) => {
  if (p?.customer) return p.customer.name || `Customer ${p.customer_id}`;
  return look.customerLabel(p?.customer_id, `Customer ${p?.customer_id ?? ''}`);
};

const emptyForm = () => ({
  customer_id: '', provision_date: new Date().toISOString().slice(0, 10),
  amount: '', expense_account_id: '', reason: '',
});

// Bad-debt provisions raised against a student's outstanding balance -
// this ledger section's field contract isn't exercised anywhere else in
// this repo, so the create payload follows the established
// customer_id/date/amount/reason convention used by invoices and receipts.
// Same DRAFT -> APPROVED -> POSTED lifecycle; approve/void run as checker,
// create/post as maker.
const ProvisionsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.customer_id) {
      showSnackbar('Student is required', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.expense_account_id) {
      showSnackbar('Bad-debt expense account is required', 'error');
      return;
    }
    const payload = {
      customer_id: Number(values.customer_id),
      provision_date: values.provision_date,
      amount: Number(values.amount),
      expense_account_id: Number(values.expense_account_id),
    };
    if (values.reason?.trim()) payload.reason = values.reason.trim();
    try {
      setSaving(true);
      await ledgerAPI.createArProvision(payload);
      showSnackbar('Provision created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this provision?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Provision ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getArProvision(row.provision_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch provision'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidArProvision(voidDlg.item.provision_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Provision voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.RECEIVABLES}
        title="Bad-Debt Provisions"
        subtitle="Provisions raised against a student's outstanding balance"
        addLabel="New Provision"
        idKey="provision_id"
        searchPlaceholder="Search provision #, student..."
        emptyMessage="No provisions found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getArProvisions(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'customer_id', label: 'Student', picker: CustomerPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Provision date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Provision #', render: (p) => <strong>{p.provision_number || `PRV-${p.provision_id}`}</strong> },
          { label: 'Student', render: (p) => studentLabel(p, look) },
          { label: 'Date', render: (p) => fmtDate(p.provision_date) },
          { label: 'Amount', render: (p) => formatAccountingAmount(p.amount), align: 'right' },
          { label: 'Status', render: (p) => <ArStatusChip status={p.status} /> },
        ]}
        rowActions={(p) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(p) }];
          if (p.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveArProvision(p.provision_id)) });
          }
          if (p.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postArProvision(p.provision_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(p.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: p, void_reason: '' }) });
          }
          if (p.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete provision',
                message: `Delete DRAFT provision ${p.provision_number || `PRV-${p.provision_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteArProvision(p.provision_id);
                    showSnackbar('Provision deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Provision</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <CustomerPicker label="Student" value={values.customer_id} onChange={(id) => setValues((p) => ({ ...p, customer_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="date" label="Provision Date" value={values.provision_date}
                onChange={(e) => setValues((p) => ({ ...p, provision_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Bad-debt expense account" value={values.expense_account_id} onChange={(id) => setValues((p) => ({ ...p, expense_account_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Reason" value={values.reason}
                onChange={(e) => setValues((p) => ({ ...p, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Provision {detail?.provision_number || (detail?.provision_id ? `PRV-${detail.provision_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Student: <strong>{studentLabel(detail, look)}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Date: {fmtDate(detail.provision_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Expense account: {look.accountLabel(detail.expense_account_id, detail.expense_account_id)}</Typography>
              {detail.reason && <Typography variant="body2" sx={{ mb: 1 }}>Reason: {detail.reason}</Typography>}
              <Box sx={{ mt: 1 }}><ArStatusChip status={detail.status} /></Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>Voided: {detail.void_reason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void provision ${voidDlg.item?.provision_number || (voidDlg.item ? `PRV-${voidDlg.item.provision_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default ProvisionsTab;
